import React, {useEffect, useRef, useState} from 'react';
import {message, Progress} from 'antd';
import {useTranslation} from 'react-i18next';
import {useBulkJobsState} from '../../contexts/BulkJobsContext';
import {bulkJobIsTerminal} from '../helpers/removalOutcome';

// Observation and completion refreshes live in BulkJobMonitor; this row only
// owns its toast and closes it when the tree removes the row.
const AssetGroupBulkJobProgress = ({bulkJobId, onFinish, onError})=> {
    const {t} = useTranslation();
    const {bulkJobs} = useBulkJobsState()
    const bulkJob = bulkJobs[bulkJobId]

    const ref = useRef()
    const handledTerminalJob = useRef()

    const bulkJobError = bulkJob?.error;

    const [closed, setClosed] = useState(false)
    const click = ()=> {
        ref.current()
        setClosed(true)
    }

    useEffect(() => {
        setClosed(false)
        return () => {ref.current?.()}
    }, [bulkJobId])

    useEffect(()=>{
        if(!bulkJob) return;

        if(bulkJob.canceled || bulkJob.aasm_state === 'canceled') {
            const canceledKey = `${bulkJobId}:canceled`;
            if(handledTerminalJob.current === canceledKey) return;
            handledTerminalJob.current = canceledKey;
            ref.current?.()
            message.open({
                key: `bulk-job-${bulkJobId}`,
                type: 'warning',
                content: t('message-bulk-job-canceled', 'Bulk job canceled.'),
                duration: 4
            })
            onError?.(bulkJob)
            return;
        }

        if(bulkJobError) {
            const errorKey = `${bulkJobId}:${bulkJob.aasm_state}:${bulkJob.updated_at || bulkJobError}`;
            if(handledTerminalJob.current === errorKey) return;
            handledTerminalJob.current = errorKey;

            message.open({
                key: `bulk-job-${bulkJobId}`,
                type: 'error',
                content: (
                    <>
                        {t('bulk-job-failed','Bulk job failed')}: {bulkJobError}
                    </>
                ),
                duration: 8
            })
            if(bulkJobIsTerminal(bulkJob)) onError && onError(bulkJob);
            return;
        }

        const doneKey = `${bulkJobId}:done`;
        if(bulkJob.done && handledTerminalJob.current === doneKey) return;
        handledTerminalJob.current = bulkJob.done ? doneKey : null;

        if(bulkJob.done) {
            ref.current?.()
            setClosed(false)
            // onFinish means the operation finished. The monitor continues
            // observing any remaining indexing and refreshes counts again.
            if(bulkJob.asset_group_move && bulkJob.indexing_pending) {
                message.info({
                    key: `bulk-job-${bulkJobId}-indexing`,
                    content: bulkJob.indexing_status || t('asset-group-move-indexing-pending', 'Move complete. Search and counts are catching up.'),
                    duration: 8
                })
            }
            onFinish?.(bulkJob);
            return;
        }

        if(closed) return;

        ref.current = message.open({
            key: `bulk-job-${bulkJobId}`,
            type: 'loading',
            onClick: click,
            content: (
                <>
                    {bulkJob.progress == 100 && (
                        <>
                            {t('updating-index','Updating Index...')}
                        </>
                    ) || (
                        <>
                            {t('processing','Processing')}: <Progress percent={bulkJob.progress || 0} status={'active'}/>
                        </>
                    )}
                </>
            ),
            duration: 0
        })

    }, [bulkJobId, bulkJob?.progress, bulkJob?.done, bulkJob?.aasm_state, bulkJob?.canceled, bulkJob?.operation_counts, bulkJobError, closed])

    return <></>
}

export default AssetGroupBulkJobProgress;
